import { useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon/Icon';
import { Button } from '../Button/Button';
import { Modal } from '../Modal/Modal';
import { useToast } from '../Toast/Toast';
import { SettingsCard, SettingsRow } from '../SettingsShell/SettingsPage';
import {
  GMAIL_ACCOUNT, connectGmail, disconnectGmail, useGmailStore,
} from '../../settings-gmail/store';

/** The status pill on the card header (spec 38 §1). */
function StatusPill({ state }: { state: 'none' | 'connected' | 'expired' }) {
  const map = {
    none: { label: 'Not connected', fg: 'var(--ink-3)', bg: 'var(--surface-mute)' },
    connected: { label: 'Connected', fg: 'var(--success)', bg: 'var(--success-tint)' },
    expired: { label: 'Needs reconnecting', fg: 'var(--alert-red)', bg: '#FBEDEC' },
  }[state];
  return (
    <span className="rounded-[var(--r-pill)] px-2.5 py-1 text-[11px] font-semibold" style={{ color: map.fg, background: map.bg }}>
      {map.label}
    </span>
  );
}

/** A grey block: what ELVA does with the inbox (§1.5), shown in none + connected. */
function WhatElvaDoes() {
  return (
    <div className="border-t border-[var(--cream-2)] bg-[var(--surface-sunken)] px-6 py-4 text-[12.5px] leading-[1.7] text-[var(--text-secondary)]">
      <ul className="m-0 list-disc space-y-1 pl-4">
        <li>Reads new emails that arrive in this inbox and shows patients’ emails on the Email page</li>
        <li>Sends AI Email’s replies from this account — they appear in its Sent folder</li>
        <li>Reminders, recalls and other agent emails don’t use it: ELVA sends those itself</li>
        <li>Your team can keep using Gmail as usual</li>
      </ul>
    </div>
  );
}

/**
 * Card 00 — Your Gmail inbox (spec 38). AI Email works through the practice's
 * Gmail: ELVA reads that inbox and sends AI Email's replies from it. One account
 * per practice; only an admin may connect, switch or disconnect. Staging skips
 * Google — Connect shows the waiting modal for ~2s, then lands on Connected.
 */
export function GmailConnectCard() {
  const { state, admin } = useGmailStore();
  const { toast } = useToast();
  const [connecting, setConnecting] = useState(false);
  const [confirm, setConfirm] = useState<null | 'disconnect' | 'switch'>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timer.current !== null) clearTimeout(timer.current); }, []);

  // Staging: open the waiting modal, then land on Connected after ~2s.
  const startConnect = () => {
    setConnecting(true);
    timer.current = setTimeout(() => {
      connectGmail();
      setConnecting(false);
      toast({ title: `Gmail connected — ${GMAIL_ACCOUNT.address}`, variant: 'success' });
    }, 2000);
  };
  const cancelConnect = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    setConnecting(false);
  };
  const doDisconnect = () => {
    disconnectGmail();
    setConfirm(null);
    toast({ title: 'Gmail disconnected', variant: 'success' });
  };

  return (
    <>
      <SettingsCard
        n="00"
        id="gmail"
        title="Your Gmail inbox"
        sub="One Gmail or Google Workspace account. ELVA reads it and answers patients from it."
        headRight={<StatusPill state={state} />}
      >
        {state === 'none' ? (
          <div className="px-6 py-5">
            <p className="m-0 max-w-[640px] text-[13px] leading-[1.6] text-[var(--text-secondary)]">
              Connect the inbox patients already write to. You’ll sign in with Google and allow ELVA to read and send
              email for that account. One account only.
            </p>
            {admin ? (
              <div className="mt-4">
                <Button variant="primary" size="sm" iconLeft="mail" onClick={startConnect}>Connect Gmail</Button>
              </div>
            ) : (
              <p className="mt-3 text-[12px] text-[var(--ink-3)]">Only a practice admin can connect, switch or disconnect the inbox.</p>
            )}
          </div>
        ) : null}

        {state === 'connected' ? (
          <>
            <SettingsRow label="Account" control={
              <span className="text-right">
                <span className="block font-[var(--font-mono)] text-[13px] font-semibold text-[var(--text-primary)]">{GMAIL_ACCOUNT.address}</span>
                <span className="mt-0.5 block text-[10.5px] text-[var(--text-muted)]">{GMAIL_ACCOUNT.kind}</span>
              </span>
            } />
            <SettingsRow label="Connected by" control={<span className="text-[12.5px] text-[var(--text-secondary)]">{GMAIL_ACCOUNT.connectedBy}</span>} />
            <SettingsRow last label="Last checked" control={<span className="text-[12.5px] text-[var(--text-secondary)]">{GMAIL_ACCOUNT.lastChecked}</span>} />
            {admin ? (
              <div className="flex flex-wrap items-center gap-3 border-t border-[var(--cream-2)] px-6 py-4">
                <Button variant="secondary" size="sm" onClick={() => { setConfirm('switch'); }}>Switch account</Button>
                <button type="button" onClick={() => { setConfirm('disconnect'); }}
                  className="rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--alert-red)_45%,transparent)] px-3 py-1.5 text-[12.5px] font-semibold text-[var(--alert-red)] hover:bg-[#FBEDEC]">
                  Disconnect
                </button>
                <span className="ml-auto text-[11.5px] text-[var(--ink-4)]">Only one account can be connected.</span>
              </div>
            ) : (
              <div className="border-t border-[var(--cream-2)] px-6 py-3 text-[12px] text-[var(--ink-3)]">Only a practice admin can connect, switch or disconnect the inbox.</div>
            )}
          </>
        ) : null}

        {state === 'expired' ? (
          <>
            <div className="mx-6 mt-5 flex items-start gap-3 rounded-[12px] border border-[color-mix(in_srgb,var(--alert-red)_30%,transparent)] bg-[#FBEDEC] px-4 py-3">
              <Icon kind="alert" size={15} stroke={2} className="mt-0.5 shrink-0 text-[var(--alert-red)]" />
              <p className="m-0 text-[12.5px] leading-[1.6] text-[var(--alert-red)]">
                Google stopped ELVA’s access on {GMAIL_ACCOUNT.stoppedAt}. ELVA isn’t reading or answering this inbox.
                Reconnect with the same account to pick up where it left off.
              </p>
            </div>
            <div className="mt-3">
              <SettingsRow label="Account" control={<span className="font-[var(--font-mono)] text-[13px] font-semibold text-[var(--text-primary)]">{GMAIL_ACCOUNT.address}</span>} />
              <SettingsRow last label="Last checked" control={<span className="text-[12.5px] text-[var(--text-secondary)]">{GMAIL_ACCOUNT.lastChecked}</span>} />
            </div>
            {admin ? (
              <div className="flex flex-wrap items-center gap-3 border-t border-[var(--cream-2)] px-6 py-4">
                <Button variant="primary" size="sm" iconLeft="mail" onClick={startConnect}>Reconnect</Button>
                <button type="button" onClick={() => { setConfirm('disconnect'); }}
                  className="rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--alert-red)_45%,transparent)] px-3 py-1.5 text-[12.5px] font-semibold text-[var(--alert-red)] hover:bg-[#FBEDEC]">
                  Disconnect
                </button>
              </div>
            ) : (
              <div className="border-t border-[var(--cream-2)] px-6 py-3 text-[12px] text-[var(--ink-3)]">Only a practice admin can connect, switch or disconnect the inbox.</div>
            )}
          </>
        ) : null}

        {state === 'none' || state === 'connected' ? <WhatElvaDoes /> : null}
      </SettingsCard>

      {/* Connecting — the waiting modal (§1.2). Google's own screens are never imitated. */}
      {connecting ? (
        <Modal open title="Finish in the Google window" onClose={cancelConnect}>
          <p className="m-0 text-[13px] leading-[1.6] text-[var(--ink-1)]">
            Sign in and allow access in the window Google opened. This page updates by itself.
          </p>
          <div className="mt-4 flex items-center justify-between gap-2">
            <button type="button" onClick={startConnect} className="text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline">Open it again</button>
            <Button variant="secondary" size="sm" onClick={cancelConnect}>Cancel</Button>
          </div>
        </Modal>
      ) : null}

      {confirm === 'disconnect' ? (
        <Modal open title={`Disconnect ${GMAIL_ACCOUNT.address}?`} onClose={() => { setConfirm(null); }}>
          <p className="m-0 text-[13px] leading-[1.6] text-[var(--ink-1)]">
            ELVA stops reading and answering this inbox, and AI Email stops. Emails already in ELVA stay. Reminders and
            other agent emails keep going — ELVA sends those itself.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => { setConfirm(null); }}>Keep it connected</Button>
            <Button variant="danger" size="sm" onClick={doDisconnect}>Disconnect</Button>
          </div>
        </Modal>
      ) : null}

      {confirm === 'switch' ? (
        <Modal open title="Switch to another Gmail account?" onClose={() => { setConfirm(null); }}>
          <p className="m-0 text-[13px] leading-[1.6] text-[var(--ink-1)]">
            Only one account can be connected. ELVA stops reading {GMAIL_ACCOUNT.address} and starts with the account you
            sign in with next. Emails already in ELVA stay.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => { setConfirm(null); }}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={() => { setConfirm(null); startConnect(); }}>Sign in with another account</Button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
